import React from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import {
  ChefHat, LayoutDashboard, UtensilsCrossed, ShieldCheck,
  QrCode, LogOut, Store, Crown, CreditCard, Settings,
  Package, BarChart3, Compass, Gift, Monitor
} from 'lucide-react';

export default function DashboardPage() {
  const { user, restaurant, logout } = useAuth();

  const links = [
    ...(user?.role === 'super_admin' ? [
      { to: '/super-admin', icon: Crown, label: 'Super Admin', desc: 'Gestion SaaS : utilisateurs, restaurants, abonnements', color: '#EF4444' },
    ] : []),
    { to: '/admin', icon: ShieldCheck, label: 'Admin / Manager', desc: 'Gestion complete du restaurant', color: '#0A422D' },
    { to: '/caisse', icon: Monitor, label: 'Caisse', desc: 'Point de vente tactile (ecran 15")', color: '#E16833' },
    { to: '/server', icon: LayoutDashboard, label: 'POS Serveur', desc: 'Prise de commande & encaissement', color: '#2D6A4F' },
    { to: '/kitchen', icon: UtensilsCrossed, label: 'Cuisine & Bar', desc: 'Preparation des commandes', color: '#E16833' },
    { to: '/table/1', icon: QrCode, label: 'Menu Client', desc: 'Apercu du menu digital (Table 1)', color: '#D4A853' },
  ];

  const tools = [
    { to: '/analytics', icon: BarChart3, label: 'Analytics', color: '#7C3AED' },
    { to: '/stock', icon: Package, label: 'Stocks', color: '#0EA5E9' },
    { to: '/subscription', icon: CreditCard, label: 'Abonnement', color: '#D4A853' },
    { to: '/referral', icon: Gift, label: 'Parrainage', color: '#E16833' },
    { to: '/settings', icon: Settings, label: 'Parametres', color: '#6E5E52' },
    { to: '/onboarding', icon: Compass, label: 'Configuration', color: '#2D6A4F' },
  ];

  return (
    <div className="min-h-screen bg-[#0C0C0C] flex flex-col items-center justify-center p-6" data-testid="dashboard-page">
      {/* Header */}
      <div className="w-full max-w-lg mb-8 text-center">
        <div className="flex items-center justify-center gap-2 mb-3">
          <ChefHat className="w-8 h-8 text-[#D4A853]" />
          <span className="text-2xl font-black text-white tracking-tight">HIBI<span className="text-[#D4A853]">SAAS</span></span>
        </div>
        {restaurant && (
          <div className="flex items-center justify-center gap-2 mb-2">
            <Store className="w-4 h-4 text-[#888]" />
            <span className="text-sm text-[#888]">{restaurant.name}</span>
            <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-[#D4A853]/10 text-[#D4A853] uppercase">{restaurant.plan}</span>
          </div>
        )}
        {user && (
          <p className="text-xs text-[#666]">Connecte en tant que {user.name || user.email}</p>
        )}
      </div>

      {/* Main Links */}
      <div className="w-full max-w-lg space-y-3 mb-6">
        {links.map((link) => (
          <Link key={link.to} to={link.to}
            className="flex items-center gap-4 p-4 rounded-2xl border border-[#1E1E1E] bg-[#111] hover:bg-[#1A1A1A] hover:border-[#333] transition-all group"
            data-testid={`dash-link-${link.to.replace('/', '')}`}>
            <div className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: `${link.color}15` }}>
              <link.icon className="w-6 h-6" style={{ color: link.color }} />
            </div>
            <div className="flex-1">
              <p className="font-bold text-white text-sm group-hover:text-[#D4A853] transition-colors">{link.label}</p>
              <p className="text-xs text-[#888]">{link.desc}</p>
            </div>
          </Link>
        ))}
      </div>

      {/* Tools Grid */}
      <div className="w-full max-w-lg">
        <p className="text-xs text-[#666] mb-3 px-1">Outils</p>
        <div className="grid grid-cols-6 gap-2">
          {tools.map((tool) => (
            <Link key={tool.to} to={tool.to}
              className="flex flex-col items-center gap-1.5 p-3 rounded-xl border border-[#1E1E1E] bg-[#111] hover:bg-[#1A1A1A] hover:border-[#333] transition-all"
              data-testid={`tool-${tool.to.replace('/', '')}`}>
              <tool.icon className="w-5 h-5" style={{ color: tool.color }} />
              <span className="text-[10px] text-[#888]">{tool.label}</span>
            </Link>
          ))}
        </div>
      </div>

      {/* Logout */}
      <Button variant="ghost" onClick={logout}
        className="mt-8 text-[#666] hover:text-white text-sm"
        data-testid="dashboard-logout">
        <LogOut className="w-4 h-4 mr-2" /> Deconnexion
      </Button>

      {/* Legal footer */}
      <div className="mt-6 mb-4 flex flex-wrap items-center justify-center gap-x-4 gap-y-1 text-[11px] text-[#555]">
        <Link to="/legal/terms" className="hover:text-white transition-colors">CGV</Link>
        <Link to="/legal/terms_of_use" className="hover:text-white transition-colors">CGU</Link>
        <Link to="/legal/privacy" className="hover:text-white transition-colors">Confidentialite</Link>
        <Link to="/legal/legal_notice" className="hover:text-white transition-colors">Mentions legales</Link>
        <Link to="/legal/contact" className="hover:text-white transition-colors">Contact</Link>
      </div>
      <p className="text-[10px] text-[#444] text-center mb-4">INFINITY-GZ LLC — Sharjah Media City (SHAMS), UAE</p>
    </div>
  );
}
